/**
 * Application Insights Browser Instrumentation
 * Tracks client-side performance, user interactions, and browser telemetry
 */

import {ApplicationInsights} from "@microsoft/applicationinsights-web";
import {ReactPlugin} from "@microsoft/applicationinsights-react-js";

let reactPlugin: ReactPlugin | null = null;
let appInsights: ApplicationInsights | null = null;

/**
 * Initialize Application Insights for browser/client-side tracking
 * Call this once in your app initialization (e.g., _app.tsx or layout.tsx)
 */
export function initializeAppInsights() {
  // Only run in browser (client-side)
  if (typeof window === "undefined") {
    return null;
  }

  // Prevent duplicate initialization
  if (appInsights) {
    return {appInsights, reactPlugin};
  }

  const connectionString = process.env.NEXT_PUBLIC_APPLICATIONINSIGHTS_CONNECTION_STRING;

  if (!connectionString) {
    console.warn("⚠️ NEXT_PUBLIC_APPLICATIONINSIGHTS_CONNECTION_STRING not set - browser telemetry disabled");
    
return null;
  }

  try {
    // Create React plugin for automatic component tracking
    reactPlugin = new ReactPlugin();

    // Initialize Application Insights
    appInsights = new ApplicationInsights({
      config: {
        connectionString: connectionString,
        enableAutoRouteTracking: true, // Track Next.js route changes
        enableCorsCorrelation: false, // DISABLED - Causes Response.clone() issues in Next.js
        enableRequestHeaderTracking: false, // DISABLED - Causes Response.clone() issues
        enableResponseHeaderTracking: false, // DISABLED - Causes Response.clone() issues
        correlationHeaderExcludedDomains: ["*.google-analytics.com", "*.doubleclick.net"],
        disableFetchTracking: true, // DISABLED - Using custom fetch interceptor to avoid conflicts
        disableAjaxTracking: false, // Track XMLHttpRequest calls
        autoTrackPageVisitTime: true, // Track how long users stay on pages
        enableUnhandledPromiseRejectionTracking: true, // Track unhandled promise rejections
        disableExceptionTracking: false, // Track exceptions
        loggingLevelConsole: 1, // Enable console logging (0=off, 1=critical, 2=warning)
        loggingLevelTelemetry: 1, // Enable telemetry logging
        disableInstrumentationKeyValidation: true, // Avoid validation issues
        extensions: [reactPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: {
            // React plugin configuration
          },
        },
      },
    });

    appInsights.loadAppInsights();

    // Add telemetry initializer to set cloud role and capture correlation IDs
    appInsights.addTelemetryInitializer((envelope: any) => {
      // Set cloud role name for proper service identification in Azure
      envelope.tags = envelope.tags || [];
      envelope.tags["ai.cloud.role"] = "edupop-app";
      envelope.tags["ai.cloud.roleInstance"] = typeof window !== "undefined" ? window.location.hostname : "browser";
      
      // Add custom properties to all telemetry
      envelope.data = envelope.data || {};

      // Try to get correlation ID from current request context
      if (typeof window !== "undefined") {
        const correlationId = (window as any).__correlationId;

        if (correlationId) {
          envelope.data.correlationId = correlationId;
          envelope.tags["ai.operation.id"] = correlationId.substring(0, 16); // Azure uses 16 chars
        }
      }

      return true;
    });

    // Track that initialization completed
    appInsights.trackEvent(
      {name: "AppInsights_Initialized"},
      {
        timestamp: new Date().toISOString(),
        environment: process.env.NODE_ENV || "unknown",
      }
    );

    // Set authenticated user context if available
    if (typeof window !== "undefined" && (window as any).userId) {
      appInsights.setAuthenticatedUserContext((window as any).userId);
    }

    // Application Insights initialized successfully

    return {appInsights, reactPlugin};
  } catch (error) {
    console.error("❌ Failed to initialize Application Insights:", error);
    
return null;
  }
}

/**
 * Get the Application Insights instance
 */
export function getAppInsights() {
  return appInsights;
}

/**
 * Get the React plugin instance
 */
export function getReactPlugin() {
  return reactPlugin;
}

/**
 * Track a custom event
 */
export function trackEvent(name: string, properties?: {[key: string]: any}) {
  if (appInsights) {
    appInsights.trackEvent({name}, properties);
  }
}

/**
 * Track a custom metric
 */
export function trackMetric(name: string, average: number, properties?: {[key: string]: any}) {
  if (appInsights) {
    appInsights.trackMetric({name, average}, properties);
  }
}

/**
 * Track an exception
 */
export function trackException(error: Error, severityLevel?: number) {
  if (appInsights) {
    appInsights.trackException({exception: error, severityLevel});
  }
}

/**
 * Track a page view manually (automatic tracking is enabled by default)
 */
export function trackPageView(name?: string, uri?: string) {
  if (appInsights) {
    appInsights.trackPageView({name, uri});
  }
}

/**
 * Set user context for tracking
 */
export function setUser(userId: string, accountId?: string) {
  if (appInsights) {
    appInsights.setAuthenticatedUserContext(userId, accountId);
  }
}

/**
 * Clear user context
 */
export function clearUser() {
  if (appInsights) {
    appInsights.clearAuthenticatedUserContext();
  }
}

/**
 * Test telemetry - sends a test event to verify Application Insights is working
 * Call this from browser console: window.testAppInsights()
 */
export function testTelemetry() {
  if (!appInsights) {
    console.error("❌ [APP-INSIGHTS] Not initialized!");
    
return;
  }

  console.log("🧪 [APP-INSIGHTS] Sending test telemetry...");

  // Send test page view
  appInsights.trackPageView({
    name: "Test Page View",
    uri: window.location.href,
  });
  console.log("✅ [APP-INSIGHTS] Test page view sent");

  // Send test event
  appInsights.trackEvent(
    {name: "Test_Event"},
    {
      testProperty: "test value",
      timestamp: new Date().toISOString(),
    }
  );
  console.log("✅ [APP-INSIGHTS] Test event sent");

  // Send test metric
  appInsights.trackMetric({name: "Test_Metric", average: 123});
  console.log("✅ [APP-INSIGHTS] Test metric sent");

  // Flush immediately
  appInsights.flush();
  console.log("✅ [APP-INSIGHTS] Telemetry flushed");
  console.log("🔍 Check Azure Portal in 1-2 minutes for test data");
}

// Expose test function to window for easy testing
if (typeof window !== "undefined") {
  (window as any).testAppInsights = testTelemetry;
}
